import React, { FC, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import useRateChange from '@/hooks/useRateChange'
import { useRiseColor } from '@/components/BaseRiseColor'
import useLangPrefix from '@/hooks/useLangPrefix'
import { formatAddString } from '@/utils/tools'
import { eventTracker } from '@/utils/collectEvent'
import { Tooltip } from '@mantine/core'
import {
  TreeChartItemStyled,
  HeatFloatingStyled,
  HeatFloatingCoinStyled,
  HeatFloatingRateStyled,
} from '@/blocks/Price/PriceChart/HeatChart/style'

const TreeChartItem: FC<{ leaf: any }> = ({ leaf }) => {
  const { getRiseColor } = useRiseColor()
  const { langPrefix } = useLangPrefix()
  const { formatRateChangePrice } = useRateChange()

  const adjustedFontSize = useMemo(() => {
    // 计算节点的面积
    const area = (leaf.x1 - leaf.x0) * (leaf.y1 - leaf.y0)
    // 动态计算字体大小，这里的 0.01 是一个调整系数，可以根据需要调整
    const baseFontSize = Math.max(10, Math.min(160, area * 0.0045))

    // 根据字符长度动态调整字体大小
    const symbolLength = leaf.data.symbol?.length ?? 3
    const adjustedFontSize = Math.max(baseFontSize / Math.sqrt(symbolLength + 4), 6)

    return adjustedFontSize
  }, [leaf.data.symbol?.length, leaf.x0, leaf.x1, leaf.y0, leaf.y1])

  // 当前转化后价格
  const currentPrice = formatRateChangePrice(leaf.data.price, { isUnit: true, isThousands: false })

  // 背景色
  const bgColor = leaf.data['change']
    ? Number(leaf.data['change']) > 0
      ? getRiseColor(1).color
      : getRiseColor(-1).color
    : 'transparent'

  /**
   * @description: 鼠标悬浮toolTip
   * @return {*}
   */
  const HeatFloating = (): React.ReactElement => {
    return (
      <HeatFloatingStyled>
        <HeatFloatingCoinStyled>
          <span> {leaf?.data?.symbol}:</span>
          <strong> {currentPrice}</strong>
        </HeatFloatingCoinStyled>
        <HeatFloatingRateStyled>
          {Number(leaf.data['change']) > 0 ? (
            <svg
              className="iconpark-icon"
              style={{
                color: bgColor,
              }}
            >
              <use href="#icon-12px-fill-shixinjiantou"></use>
            </svg>
          ) : (
            <svg
              className="iconpark-icon"
              style={{
                color: bgColor,
              }}
            >
              <use href="#icon-12px-jiantou-xia-fill"></use>
            </svg>
          )}

          <span>{formatAddString(leaf.data?.['change'])}</span>
        </HeatFloatingRateStyled>
      </HeatFloatingStyled>
    )
  }

  return (
    <Tooltip.Floating label={<HeatFloating />} position="top" style={{ padding: 12 }}>
      <TreeChartItemStyled
        {...{ component: 'a', href: `${langPrefix}/trade/${leaf.data.symbol}_USDT` }}
        bg={bgColor}
        left={leaf.x0}
        top={leaf.y0}
        width={leaf.x1 - leaf.x0}
        height={leaf.y1 - leaf.y0}
        size={adjustedFontSize}
        onClick={() => {
          eventTracker('price_overview_click', {
            currency_name: leaf?.data?.symbol,
          })
        }}
      >
        <span className="symbol">{leaf.data.symbol}</span>
        <span className="price">{currentPrice}</span>
        <span className="percentage">{formatAddString(leaf.data?.['change'])}</span>
      </TreeChartItemStyled>
    </Tooltip.Floating>
  )
}

export default observer(TreeChartItem)
